<template>
  <div class="schedule">
    <ReportDraftWatermark v-if="report.is_draft" />
    <h2 class="formTitle">Schedule B: In-kind Contributions, Pledges, Orders, Debts and Obligations</h2>
    <v-divider></v-divider>
    <div class="report-section">
      <h3 class="line-item-title">
        1. In-kind contributions received:
      </h3>
      <div v-if="arrayHasItems(report.user_data.contributions_inkind)">
        <v-simple-table dense>
          <thead>
          <tr>
            <th class="dateColumn">
              Date Received
            </th>
            <th>
              Contributor
            </th>
            <th>
              Employer/Occupation
            </th>
            <th>
              Description
            </th>
            <th class="amountColumn">
              Amount
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in report.user_data.contributions_inkind" :key="index">
            <td>
              {{ formatDate(item.date) }}
            </td>
            <td>
              <ContactInfo :contacts="contacts" :cont-id="item.contact_key"/>
            </td>
            <td>
              <RecipientInfo hide-label :contacts="contacts" :cont-id="item.contact_key"/>
            </td>
            <td>
              {{ item.description }}
            </td>
            <td class="amountColumn">
              Fair market value: {{ formatMoney(item.amount) }}<br/>
              Aggregate total (<span class="text-small"
                                     v-if="item.election">{{ electionFilter(item.election) }}</span>):
              {{ formatMoney(getAggregateTotal(contacts, item.contact_key, item.election)) }}
            </td>
          </tr>
          </tbody>
        </v-simple-table>
      </div>
      <div v-else class="noReport">
        No in-kind contributions reported this period
      </div>
      <v-divider></v-divider>
    </div>
    <div class="report-section">
      <h3 class="line-item-title">
        2. Pledges received but not yet paid:
      </h3>
      <div v-if="arrayHasItems(report.user_data.pledge)">
        <v-simple-table dense>
          <thead>
          <tr>
            <th class="dateColumn">
              Date Notified
            </th>
            <th>
              Pledge Maker
            </th>
            <th>
              Employer/Occupation
            </th>
            <th class="amountColumn">
              Fair Market Value
            </th>
            <th class="amountColumn">
              Aggregate Total
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in report.user_data.pledge" :key="index">
            <td>
              {{ formatDate(item.date) }}
            </td>
            <td>
              <ContactInfo :contacts="contacts" :cont-id="item.contact_key"/>
            </td>
            <td>
              <RecipientInfo hide-label :cont-id="item.contact_key" :contacts="contacts" />
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.amount) }}
            </td>
            <td class="amountColumn">
              {{ formatMoney(getAggregateTotal(contacts, item.contact_key, item.election)) }}
            </td>
          </tr>
          </tbody>
        </v-simple-table>
      </div>
      <div v-else class="noReport">
        No pledges reported this period
      </div>
      <v-divider></v-divider>
    </div>
    <div class="report-section">
      <h3 class="line-item-title">
        3. Orders placed, debts and obligations:
      </h3>
      <div v-if="arrayHasItems(report.user_data.debt)">
        <v-simple-table dense>
          <thead>
          <tr>
            <th class="dateColumn">
              First debt incurred
            </th>
            <th>
              Recipient/Vendor
            </th>
            <th v-if="!creditExpendituresAmount">
              Description
            </th>
            <th class="amountColumn">
              Balance owed (as of this reporting period)
            </th>
          </tr>
          </thead>
          <tbody v-for="(item, index) in report.user_data.debt" :key="index">
          <tr>
            <td>
              {{ formatDate(item.date) }}
            </td>
            <td>
              <contact-info :contacts="contacts" :cont-id="item.contact_key"/>
            </td>
            <td v-if="!creditExpendituresAmount">
              {{ item.description }}
            </td>
            <td class="amountColumn">
              {{ formatMoney(item.amount) }}
            </td>
          </tr>
          <template v-if="item.sub_debt && item.sub_debt.length > 0">
            <tr v-for="(sub, si) in item.sub_debt" :key="`${index}-${si}`">
              <td>{{ formatDate(sub.date) }}</td>
              <td style="padding-left: 4em;">
                <contact-info :contacts="contacts" :cont-id="sub.contact_key"/>
              </td>
              <td>
                {{ sub.description }}
              </td>
              <td class="amountColumn">
                {{ formatMoney(sub.amount) }}
              </td>
            </tr>
          </template>
          </tbody>
        </v-simple-table>
      </div>
      <div v-else class="noReport">
        No debt reported this period
      </div>
      <v-divider></v-divider>
    </div>
  </div>
</template>
<script>
import filters from "../filters";
import utils from "./report-utils";
import ContactInfo from "./ContactInfo";
import RecipientInfo from "./RecipientInfo";
import ReportDraftWatermark from "@/components/ReportDraftWatermark.vue";

export default {
  name: "ScheduleB",
  components: {RecipientInfo, ContactInfo, ReportDraftWatermark},
  props: {
    report: Object
  },
  data() {
    return {
      contacts: []
    }
  },
  computed: {
    creditExpendituresAmount() {
      return this.report.metadata.sums.expenditures.credit_expenditures?.amount
    },
  },
  methods: {
    ...filters.filters, ...utils
  },
  created() {
    this.contacts = this.report.user_data.contacts;
    this.sortByDate(this.report.user_data.pledge);
  }
}
</script>

<style scoped>.schedule { position: relative; }</style>
